// Proposal Review — Financial tab
//
// Upload a D-Tools cost copy PDF for AV/IT, Lighting, or Shades.
// The server runs the Margin Worksheet pipeline (parse → fill → recompute →
// sanity-check) and returns a fully-evaluated worksheet plus flags.
//
// The Margin Worksheet template is server-side — single source of truth.
// Users never re-upload it.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, Grid, IconButton, InputLabel, MenuItem,
  Paper, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Tooltip, Typography,
} from '@mui/material';
import {
  AccountBalance as FinanceIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Description as PdfIcon,
  CheckCircle as OkIcon,
  Warning as WarnIcon,
  ErrorOutlineOutlined as BlockIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import api from '../services/api';

// ── helpers ──────────────────────────────────────────────────────────────────
const DISCIPLINES = ['AV-IT', 'Lighting', 'Shades']; // alphabetical, per locked rule

const fmtCurrency = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
};
const fmtPct = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
};
const fmtNum = (v, dp = 0) => {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};
// Download an authenticated file via axios (so the JWT header is attached),
// then save it as a blob. Plain <a href> links don't carry the Authorization
// header from localStorage and would hit "No authentication token provided".
async function downloadAuthed(url, suggestedName) {
  const res = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([res.data], {
    type: res.headers?.['content-type'] || 'application/octet-stream',
  });
  let filename = suggestedName;
  const cd = res.headers?.['content-disposition'];
  if (cd) {
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
    if (m) filename = decodeURIComponent(m[1] || m[2] || suggestedName);
  }
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'download.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return '—'; }
};

const severityChip = (sev) => {
  const map = {
    BLOCK: { color: 'error',   icon: <BlockIcon fontSize="small" />, label: 'Block' },
    WARN:  { color: 'warning', icon: <WarnIcon  fontSize="small" />, label: 'Warn'  },
    INFO:  { color: 'info',    icon: <InfoIcon  fontSize="small" />, label: 'Info'  },
  };
  const m = map[sev] || { color: 'default', icon: null, label: sev };
  return <Chip size="small" color={m.color} icon={m.icon} label={m.label} />;
};

// ── component ────────────────────────────────────────────────────────────────
export default function ProposalReview() {
  const [discipline, setDiscipline] = useState('AV-IT');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [detailRow, setDetailRow] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get('/proposal-review/history?limit=100');
      setHistory(r.data?.rows || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file.');
      return;
    }
    setFile(f);
    setError('');
  };

  const onSubmit = async () => {
    if (!file) {
      setError('Choose a cost copy PDF first.');
      return;
    }
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('discipline', discipline);
      const r = await api.post('/proposal-review', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      setFile(null);
      // clear file input
      const inp = document.getElementById('pr-file-input');
      if (inp) inp.value = '';
      loadHistory();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      setError(msg);
      if (err.response?.data?.id) loadHistory();
    } finally {
      setUploading(false);
    }
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const sortedHistory = [...history].sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv
      : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const sortLabel = (col, label) => (
    <TableSortLabel
      active={sortBy === col}
      direction={sortBy === col ? sortDir : 'asc'}
      onClick={() => handleSort(col)}
    >{label}</TableSortLabel>
  );

  const flagSummary = (flagsJson, row) => {
    const flags = Array.isArray(flagsJson) ? flagsJson : [];
    const onClick = row ? (e) => { e.stopPropagation(); setDetailRow(row); } : undefined;
    if (!flags.length) {
      return (
        <Chip
          size="small" color="success"
          icon={<OkIcon fontSize="small" />}
          label="Clean"
          onClick={onClick}
          clickable={!!onClick}
        />
      );
    }
    const counts = flags.reduce((acc, f) => {
      acc[f.severity] = (acc[f.severity] || 0) + 1;
      return acc;
    }, {});
    return (
      <Stack direction="row" spacing={0.5}>
        {counts.BLOCK ? <Chip size="small" color="error"   label={`${counts.BLOCK} block`} onClick={onClick} clickable={!!onClick} /> : null}
        {counts.WARN  ? <Chip size="small" color="warning" label={`${counts.WARN} warn`}   onClick={onClick} clickable={!!onClick} /> : null}
        {counts.INFO  ? <Chip size="small" color="info"    label={`${counts.INFO} info`}   onClick={onClick} clickable={!!onClick} /> : null}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <FinanceIcon color="primary" />
        <Typography variant="h4">Proposal Review</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a D-Tools cost copy PDF. The system auto-fills the ACS Margin Worksheet,
        recomputes it, runs the pre-finance sanity checks, and gives you back a downloadable
        Excel file. The Margin Worksheet template is the single source of truth — no need to
        re-upload it.
      </Typography>

      {/* ── Upload card ──────────────────────────────────────────── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="pr-discipline-label">Discipline</InputLabel>
              <Select
                labelId="pr-discipline-label"
                label="Discipline"
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}
              >
                {DISCIPLINES.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={5}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<PdfIcon />}
              fullWidth
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {file ? file.name : 'Choose cost copy PDF…'}
              <input
                id="pr-file-input"
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={onPickFile}
              />
            </Button>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              onClick={onSubmit}
              disabled={uploading || !file}
              fullWidth
            >
              {uploading ? 'Processing…' : 'Run Review'}
            </Button>
          </Grid>
        </Grid>

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </Paper>

      {/* ── Latest result card ───────────────────────────────────── */}
      {result ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">
                {result.project_number || '—'}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  {result.project_type ? `(${result.project_type})` : ''} {result.salesman ? `· ${result.salesman}` : ''} · {result.discipline}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Processed {fmtDateTime(result.created_at)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => downloadAuthed(
                `/proposal-review/${result.id}/download`,
                `Margin Worksheet ${result.project_number || result.id}.xlsx`,
              ).catch(e => setError(e.response?.data?.error || e.message || 'Download failed'))}
            >
              Download Worksheet
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {[
              { label: 'Project Cost',     value: fmtCurrency(result.summary?.project_cost) },
              { label: 'Project Sell',     value: fmtCurrency(result.summary?.project_price) },
              { label: 'Project Margin',   value: fmtPct(result.summary?.project_margin_pct) },
              { label: 'Expected Profit',  value: fmtCurrency(result.summary?.expected_profit) },
              { label: 'Subtotal (no tax)',value: fmtCurrency(result.summary?.subtotal_no_tax) },
              { label: 'Sales Tax',        value: fmtCurrency(result.summary?.sales_tax) },
              { label: 'Total w/ Tax',     value: fmtCurrency(result.summary?.proposal_total_with_tax) },
              { label: 'Labor Hours',      value: fmtNum(result.summary?.labor_hours, 1) },
              { label: 'Labor GP %',       value: fmtPct(result.summary?.labor_gp_pct) },
              { label: 'Profit / Hour',    value: fmtCurrency(result.summary?.profit_per_hour) },
            ].map((c) => (
              <Grid item xs={6} sm={4} md={2.4} key={c.label}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>{c.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Sanity Checks
            {(!result.flags || result.flags.length === 0) ? (
              <Chip size="small" color="success" icon={<OkIcon fontSize="small" />} label="No flags raised" sx={{ ml: 1 }} />
            ) : (
              <Chip size="small" label={`${result.flags.length} flag${result.flags.length === 1 ? '' : 's'}`} sx={{ ml: 1 }} />
            )}
          </Typography>

          {result.flags && result.flags.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={90}>Severity</TableCell>
                    <TableCell width={220}>Rule</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.flags.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell>{severityChip(f.severity)}</TableCell>
                      <TableCell><code>{f.rule}</code></TableCell>
                      <TableCell>{f.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Paper>
      ) : null}

      {/* ── History table ─────────────────────────────────────── */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Recent Reviews</Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadHistory}><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{sortLabel('created_at', 'When')}</TableCell>
                <TableCell>{sortLabel('uploader_name', 'Uploaded By')}</TableCell>
                <TableCell>{sortLabel('discipline', 'Discipline')}</TableCell>
                <TableCell>{sortLabel('project_number', 'Project #')}</TableCell>
                <TableCell>{sortLabel('project_type', 'Type')}</TableCell>
                <TableCell>{sortLabel('salesman', 'Salesman')}</TableCell>
                <TableCell align="right">Margin %</TableCell>
                <TableCell align="right">Expected Profit</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Worksheet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingHistory ? (
                <TableRow><TableCell colSpan={11} align="center"><CircularProgress size={20} /></TableCell></TableRow>
              ) : sortedHistory.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No reviews yet. Upload a cost copy PDF to get started.
                  </Typography>
                </TableCell></TableRow>
              ) : sortedHistory.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => setDetailRow(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{fmtDateTime(row.created_at)}</TableCell>
                  <TableCell>{row.uploader_name || '—'}</TableCell>
                  <TableCell>{row.discipline}</TableCell>
                  <TableCell>{row.project_number || '—'}</TableCell>
                  <TableCell>{row.project_type || '—'}</TableCell>
                  <TableCell>{row.salesman || '—'}</TableCell>
                  <TableCell align="right">{fmtPct(row.summary_json?.project_margin_pct)}</TableCell>
                  <TableCell align="right">{fmtCurrency(row.summary_json?.expected_profit)}</TableCell>
                  <TableCell>{flagSummary(row.flags_json, row)}</TableCell>
                  <TableCell>
                    {row.status === 'failed'
                      ? <Tooltip title={row.error_message || ''}><Chip size="small" color="error" label="failed" /></Tooltip>
                      : <Chip size="small" color="success" label={row.status} />}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {row.status === 'failed' ? '—' : (
                      <Tooltip title="Download Margin Worksheet">
                        <IconButton
                          size="small"
                          onClick={() => downloadAuthed(
                            `/proposal-review/${row.id}/download`,
                            `Margin Worksheet ${row.project_number || row.id}.xlsx`,
                          ).catch(e => console.error('download failed', e))}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Detail dialog ──────────────────────────────────── */}
      <Dialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {detailRow?.project_number || 'Review'}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            {detailRow?.project_type ? `(${detailRow.project_type})` : ''}
            {detailRow?.salesman ? ` · ${detailRow.salesman}` : ''}
            {' '}· {detailRow?.discipline}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Uploaded {fmtDateTime(detailRow?.created_at)}
            {detailRow?.uploader_name ? ` by ${detailRow.uploader_name}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailRow?.status === 'failed' ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Processing failed. {detailRow.error_message || ''}
            </Alert>
          ) : null}

          {detailRow?.summary_json ? (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[
                { label: 'Project Cost',     value: fmtCurrency(detailRow.summary_json.project_cost) },
                { label: 'Project Sell',     value: fmtCurrency(detailRow.summary_json.project_price) },
                { label: 'Project Margin',   value: fmtPct(detailRow.summary_json.project_margin_pct) },
                { label: 'Expected Profit',  value: fmtCurrency(detailRow.summary_json.expected_profit) },
                { label: 'Labor Hours',      value: fmtNum(detailRow.summary_json.labor_hours, 1) },
                { label: 'Labor GP %',       value: fmtPct(detailRow.summary_json.labor_gp_pct) },
                { label: 'Profit / Hour',    value: fmtCurrency(detailRow.summary_json.profit_per_hour) },
                { label: 'Total w/ Tax',     value: fmtCurrency(detailRow.summary_json.proposal_total_with_tax) },
              ].map((c) => (
                <Grid item xs={6} sm={3} key={c.label}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                      <Typography variant="subtitle1" sx={{ mt: 0.25 }}>{c.value}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : null}

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Sanity Checks
            {(!detailRow?.flags_json || detailRow.flags_json.length === 0) ? (
              <Chip size="small" color="success" icon={<OkIcon fontSize="small" />} label="No flags raised" sx={{ ml: 1 }} />
            ) : (
              <Chip size="small" label={`${detailRow.flags_json.length} flag${detailRow.flags_json.length === 1 ? '' : 's'}`} sx={{ ml: 1 }} />
            )}
          </Typography>

          {detailRow?.flags_json && detailRow.flags_json.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={90}>Severity</TableCell>
                    <TableCell width={220}>Rule</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRow.flags_json.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell>{severityChip(f.severity)}</TableCell>
                      <TableCell><code>{f.rule}</code></TableCell>
                      <TableCell>{f.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DialogContent>
        <DialogActions>
          {detailRow && detailRow.status !== 'failed' ? (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => downloadAuthed(
                `/proposal-review/${detailRow.id}/download`,
                `Margin Worksheet ${detailRow.project_number || detailRow.id}.xlsx`,
              ).catch(e => console.error('download failed', e))}
            >
              Download Worksheet
            </Button>
          ) : null}
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
